import Joi from '@hapi/joi';
import { isNil } from 'lodash';
import React, { useCallback } from 'react';
import { FormattedNumber } from 'react-intl';
import { DefaultMaintenanceMeasure } from '../../../../../common/src/domain/defaultMaintenanceMeasure/defaultMaintenanceMeasureTableSchema';
import { MaintenanceMeasure } from '../../../../../common/src/domain/maintenanceMeasure/maintenanceMeasureTableSchema';
import { Survey } from '../../../../../common/src/domain/survey/survey/surveyTableSchema';
import { Unit } from '../../../../../common/src/domain/unit/unitTableSchema';
import { useTranslate } from '../../../mixins/translate/translate';
import { fetchDelete, fetchGet } from '../../../store/fetch';
import { InspectionStandardTypes } from '../../../utils/enums/InspectionStandardTypes';
import { IconRemoveButton } from '../../common/iconButtons/IconRemoveButton';
import {
    EditPanel,
    EditPanelButton,
    EditPanelButtons,
    EditPanelCell,
    EditPanelRow,
    PanelButtonLink,
    PanelButtons,
    PanelTable,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
} from '../../elements';
import { NullableFormattedDate } from '../../utils/NullableFormattedDate';
import { UnitOfMeasurement } from '../../utils/UnitOfMeasurement';
import {
    ValidatingCulturedFormInputNumber,
    ValidatingEditPanelSaveButton,
    ValidatingFilterDropdownWithUrl,
    ValidatingForm,
    ValidatingFormInputSelect,
    ValidatingFormInputText,
    ValidatingFormTextArea,
    ValidatingFailureModeDropdown,
} from '../../validatingElements';

type MaintenanceMeasureView = Pick<
    MaintenanceMeasure,
    | 'id'
    | 'description'
    | 'cost'
    | 'planYear'
    | 'surveyId'
    | 'finalPlanYear'
    | 'costSurcharge'
    | 'defaultMaintenanceMeasureId'
    | 'maintenanceType'
    | 'cycle'
    | 'previousMaintenanceMeasureId'
    | 'scheduledYear'
    | 'implementationYear'
    | 'isCurrent'
    | 'unitId'
    | 'planYearRemarks'
    | 'scheduledYearRemarks'
> & { survey: Pick<Survey, 'surveryedOn'> } & { defaultMaintenanceMeasure: Pick<DefaultMaintenanceMeasure, 'material'> } & {
    unit: Pick<Unit, 'quantity' | 'quantityUnitOfMeasurement'>;
};

const schema = Joi.object().keys({
    description: Joi.string().allow(null, ''),
    cost: Joi.number().allow(null, ''),
    planYear: Joi.number().integer().required(),
    workType: Joi.string().allow(null, ''),
    maintenanceType: Joi.string().allow(null, ''),
    urgency: Joi.string().allow(null, ''),
    finalPlanYear: Joi.number().integer().allow(null, ''),
    costSurcharge: Joi.number().allow(null, ''),
    planYearRemarks: Joi.string().allow(null, ''),
    failureModeId: Joi.string().allow(null, ''),
    defectId: Joi.string().allow(null, ''),
    location: Joi.string().allow(null, ''),
    quantity: Joi.string().allow(null, ''),
});

enum FormMode {
    None = 'None',
    AddCorrectiveMaintenanceMeasurement = 'AddCorrectiveMaintenanceMeasurement',
    EditCorrectiveMaintenanceMeasurement = 'EditCorrectiveMaintenanceMeasurement',
    ShowRelatedCorrectiveMaintenanceMeasurement = 'ShowRelatedCorrectiveMaintenanceMeasurement',
    AddOverrideDefaultMaintenanceMeasurement = 'AddOverrideDefaultMaintenanceMeasurement',
    EditOverrideDefaultMaintenanceMeasurement = 'EditOverrideDefaultMaintenanceMeasurement',
    EditDefaultMaintenanceMeasurement = 'EditDefaultMaintenanceMeasurement',
    ShowRelatedDefaultMaintenanceMeasurement = 'ShowRelatedDefaultMaintenanceMeasurement',
    ShowImplementedScheduledMaintenanceMeasurement = 'ShowImplementedScheduledMaintenanceMeasurement',
    EditObjectDefaultMaintenanceMeasurement = 'EditObjectDefaultMaintenanceMeasurement',
}

interface ISurveyFmecaMaintenanceMeasuresState {
    selectedMaintenanceMeasure?: undefined | MaintenanceMeasureView;
    showMaintenanceMeasureEdit: boolean;
    formMode: FormMode;
}

interface ISurveyFmecaMaintenanceMeasuresProps {
    surveyId: string;
    unitId?: string | undefined;
    manifestationId?: string | undefined;
    inspectionStandardType: InspectionStandardTypes;
    failureModeId?: string | undefined;
    defectId?: string | undefined;
    surveyIsFmecaAvailable: boolean;
}
const formModeNoneState = { selectedMaintenanceMeasure: undefined, showMaintenanceMeasureEdit: false, formMode: FormMode.None };

export function SurveyFmecaNenMaintenanceMeasures(props: ISurveyFmecaMaintenanceMeasuresProps) {
    const translate = useTranslate();
    const [maintenanceMeasures, setMaintenanceMeasures] = React.useState<MaintenanceMeasureView[]>([]);
    const [relatedMaintenanceMeasure, setRelatedMaintenanceMeasure] = React.useState<MaintenanceMeasureView | undefined>();
    const [currentMaintenanceMeasure, setCurrentMaintenanceMeasure] = React.useState<MaintenanceMeasureView | undefined>();
    const [state, setState] = React.useState<ISurveyFmecaMaintenanceMeasuresState>({
        showMaintenanceMeasureEdit: false,
        formMode: FormMode.None,
    });

    const fetchData = useCallback(async () => {
        const projectResponse = await fetchGet(`/api/surveys/${props.surveyId}/maintenanceMeasures`, {
            unitId: props.manifestationId ? undefined : props.unitId,
            manifestationId: props.manifestationId,
            failureModeId: props.failureModeId,
            defectId: props.defectId,
            includeAllByObject: 'true',
            isActiveOnly: 'true',
        });
        setMaintenanceMeasures(projectResponse.json);
    }, [props.surveyId, props.manifestationId, props.unitId, props.failureModeId, props.defectId]);

    React.useEffect(() => {
        setState((previousState) => ({
            ...previousState,
            ...formModeNoneState,
        }));
        void fetchData();
    }, [fetchData]);

    async function removeMaintenanceMeasure(id: string) {
        setState({ ...state, ...formModeNoneState });
        await fetchDelete(`/api/surveys/${props.surveyId}/maintenanceMeasures/${id}`);
        await fetchData();
    }

    async function maintenanceMeasureSaved() {
        setState({ ...state, ...formModeNoneState });
        setRelatedMaintenanceMeasure(undefined);
        await fetchData();
    }

    function showAddMaintenanceMeasure() {
        setRelatedMaintenanceMeasure(undefined);
        setState({
            ...state,
            ...{ selectedMaintenanceMeasure: undefined, showMaintenanceMeasureEdit: true, formMode: FormMode.AddCorrectiveMaintenanceMeasurement },
        });
    }

    async function onRowClick(maintenanceMeasure: MaintenanceMeasureView) {
        let formMode: FormMode;
        let selectedMaintenanceMeasure: MaintenanceMeasureView | undefined;

        if (!maintenanceMeasure.isCurrent) {
            formMode = FormMode.ShowRelatedDefaultMaintenanceMeasurement;
            selectedMaintenanceMeasure = maintenanceMeasure;
        } else {
            if (!maintenanceMeasure.surveyId) {
                selectedMaintenanceMeasure = maintenanceMeasure;
                formMode = FormMode.EditObjectDefaultMaintenanceMeasurement;
            } else if (maintenanceMeasure.implementationYear !== null || maintenanceMeasure.scheduledYear !== null) {
                formMode = FormMode.ShowImplementedScheduledMaintenanceMeasurement;
                selectedMaintenanceMeasure = maintenanceMeasure;
            } else {
                if (maintenanceMeasure.surveyId === props.surveyId) {
                    selectedMaintenanceMeasure = maintenanceMeasure;
                    if (maintenanceMeasure.defaultMaintenanceMeasureId) {
                        if (maintenanceMeasure.previousMaintenanceMeasureId === null) {
                            formMode = FormMode.EditDefaultMaintenanceMeasurement;
                        } else {
                            formMode = FormMode.EditOverrideDefaultMaintenanceMeasurement;
                        }
                    } else {
                        formMode = FormMode.EditCorrectiveMaintenanceMeasurement;
                    }
                } else {
                    if (maintenanceMeasure.defaultMaintenanceMeasureId) {
                        selectedMaintenanceMeasure = undefined;
                        formMode = FormMode.AddOverrideDefaultMaintenanceMeasurement;
                    } else {
                        selectedMaintenanceMeasure = maintenanceMeasure;
                        formMode = FormMode.ShowRelatedCorrectiveMaintenanceMeasurement;
                    }
                }
            }
        }

        if (formMode === FormMode.EditDefaultMaintenanceMeasurement || formMode === FormMode.EditObjectDefaultMaintenanceMeasurement) {
            const response = await fetchGet(`/api/surveys/${props.surveyId}/maintenanceMeasures/${maintenanceMeasure.id}`);
            setCurrentMaintenanceMeasure(response.json);
            setRelatedMaintenanceMeasure(undefined);
        } else if (
            formMode === FormMode.ShowRelatedCorrectiveMaintenanceMeasurement ||
            formMode === FormMode.AddOverrideDefaultMaintenanceMeasurement
        ) {
            const response = await fetchGet(`/api/surveys/${props.surveyId}/maintenanceMeasures/${maintenanceMeasure.id}`);
            setRelatedMaintenanceMeasure(response.json);
        } else if (formMode === FormMode.EditOverrideDefaultMaintenanceMeasurement) {
            const response = await fetchGet(`/api/surveys/${props.surveyId}/maintenanceMeasures/${maintenanceMeasure.previousMaintenanceMeasureId}`);
            setRelatedMaintenanceMeasure(response.json);
        } else if (formMode === FormMode.ShowRelatedDefaultMaintenanceMeasurement) {
            const response = await fetchGet(`/api/surveys/${props.surveyId}/maintenanceMeasures/${maintenanceMeasure.id}/current`);
            setRelatedMaintenanceMeasure(response.json);
        } else if (formMode === FormMode.ShowImplementedScheduledMaintenanceMeasurement) {
            const response = await fetchGet(`/api/surveys/${props.surveyId}/maintenanceMeasures/${maintenanceMeasure.id}`);
            setCurrentMaintenanceMeasure(response.json);
            setRelatedMaintenanceMeasure(undefined);
        } else {
            setCurrentMaintenanceMeasure(undefined);
            setRelatedMaintenanceMeasure(undefined);
        }

        setState({ ...state, ...{ selectedMaintenanceMeasure, showMaintenanceMeasureEdit: true, formMode } });
    }

    function handleCancel() {
        setState({ ...state, ...{ selectedMaintenanceMeasure: undefined, showMaintenanceMeasureEdit: false, formMode: FormMode.None } });
    }

    function canDelete(maintenanceMeasure: MaintenanceMeasureView) {
        if (maintenanceMeasure.surveyId !== props.surveyId) {
            return false;
        }
        if (!maintenanceMeasure.surveyId) {
            return false;
        }
        if (!maintenanceMeasure.isCurrent) {
            return false;
        }
        if (maintenanceMeasure.implementationYear || maintenanceMeasure.scheduledYear) {
            return false;
        }
        return true;
    }

    return (
        <EditPanel noPadding={true}>
            <PanelTable hover={true}>
                <TableHead>
                    <TableHeadRow>
                        <TableHeader>{translate('maintenanceMeasure.maintenanceMeasure')}</TableHeader>
                        <TableHeader>{translate('maintenanceMeasure.maintenanceType')}</TableHeader>
                        <TableHeader>{translate('common.cost')}</TableHeader>
                        <TableHeader>{translate('maintenanceMeasure.year')}</TableHeader>
                        <TableHeader>{translate('maintenanceMeasure.cycle')}</TableHeader>
                        <TableHeader>&nbsp;</TableHeader>
                    </TableHeadRow>
                </TableHead>
                <TableBody>
                    {maintenanceMeasures.map((maintenanceMeasure) => (
                        <TableRow
                            key={maintenanceMeasure.id}
                            isRowSelected={state.selectedMaintenanceMeasure && maintenanceMeasure.id === state.selectedMaintenanceMeasure.id}
                            onClick={async () => onRowClick(maintenanceMeasure)}
                        >
                            <TableData>{maintenanceMeasure.description}</TableData>
                            <TableData>
                                {(maintenanceMeasure.maintenanceType && <span>{maintenanceMeasure.maintenanceType} </span>) || (
                                    <span>{translate('maintenanceMeasure.correctiveMaintenance')}</span>
                                )}
                            </TableData>
                            <TableData>
                                <FormattedNumber value={maintenanceMeasure.cost ?? 0} style="currency" currency="EUR" />
                            </TableData>
                            <TableData>
                                {maintenanceMeasure.planYear}
                                {!isNil(maintenanceMeasure.finalPlanYear) && <span>-{maintenanceMeasure.finalPlanYear}</span>}
                            </TableData>
                            <TableData>
                                {maintenanceMeasure.cycle && (
                                    <>
                                        {' '}
                                        <FormattedNumber value={maintenanceMeasure.cycle} /> {translate('common.year')}{' '}
                                    </>
                                )}
                            </TableData>
                            <TableData>
                                {canDelete(maintenanceMeasure) && (
                                    <IconRemoveButton onClick={async () => removeMaintenanceMeasure(maintenanceMeasure.id)} />
                                )}
                            </TableData>
                        </TableRow>
                    ))}
                </TableBody>
            </PanelTable>

            {state.formMode === FormMode.None && (
                <PanelButtons>
                    <PanelButtonLink
                        onClick={() => showAddMaintenanceMeasure()}
                        small={true}
                        primary={true}
                        text={translate('common.addItem', translate('maintenanceMeasure.maintenanceMeasure'))}
                    />
                </PanelButtons>
            )}
            {state.formMode === FormMode.AddCorrectiveMaintenanceMeasurement && (
                <ValidatingForm
                    schema={schema}
                    createUrl={`/api/surveys/${props.surveyId}/maintenanceMeasures`}
                    createUrlQueryParameters={{
                        unitId: props.manifestationId ? undefined : props.unitId,
                        manifestationId: props.manifestationId,
                        failureModeId: props.failureModeId,
                        defectId: props.defectId,
                    }}
                    onSubmitted={async () => maintenanceMeasureSaved()}
                    subForm={true}
                >
                    <EditPanelRow>
                        <EditPanelCell rowCount={8}>
                            <ValidatingFormInputText name="description" topLabel={translate('common.description')} />
                        </EditPanelCell>
                        <EditPanelCell rowCount={4}>
                            <ValidatingFormInputText name="location" topLabel={translate('common.location')} />
                        </EditPanelCell>
                    </EditPanelRow>
                    <EditPanelRow>
                        <EditPanelCell rowCount={2}>
                            <ValidatingCulturedFormInputNumber name="planYear" topLabel={translate('maintenanceMeasure.year')} />
                        </EditPanelCell>
                        <EditPanelCell rowCount={2}>
                            <ValidatingCulturedFormInputNumber name="finalPlanYear" topLabel={translate('maintenanceMeasure.finalPlanYear')} />
                        </EditPanelCell>
                        <EditPanelCell rowCount={2}>
                            <ValidatingCulturedFormInputNumber name="cost" topLabel={translate('common.cost')} />
                        </EditPanelCell>
                        <EditPanelCell rowCount={6}>
                            <ValidatingFormInputText name="quantity" topLabel={translate('common.quantity')} />
                        </EditPanelCell>
                    </EditPanelRow>
                    {props.inspectionStandardType === InspectionStandardTypes.Fmeca && (
                        <EditPanelRow>
                            <EditPanelCell rowCount={4}>
                                <ValidatingFormInputSelect
                                    name="maintenanceType"
                                    topLabel={translate('maintenanceMeasure.maintenanceType')}
                                    placeholder={translate('common.selectItem', translate('maintenanceMeasure.maintenanceType'))}
                                    options={[
                                        {
                                            text: translate('maintenanceMeasure.correctiveMaintenance'),
                                            value: translate('maintenanceMeasure.correctiveMaintenance'),
                                        },
                                        {
                                            text: translate('maintenanceMeasure.preventiveMaintenance'),
                                            value: translate('maintenanceMeasure.preventiveMaintenance'),
                                        },
                                    ]}
                                />
                            </EditPanelCell>
                        </EditPanelRow>
                    )}
                    {props.surveyIsFmecaAvailable &&
                        props.unitId &&
                        props.inspectionStandardType === InspectionStandardTypes.Fmeca &&
                        props.failureModeId === undefined && (
                            <EditPanelRow>
                                <EditPanelCell rowCount={12}>
                                    <ValidatingFailureModeDropdown
                                        name="failureModeId"
                                        surveyId={props.surveyId}
                                        unitId={props.unitId}
                                        manifestationId={props.manifestationId}
                                        failureModeId={props.failureModeId}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                        )}
                    {!props.surveyIsFmecaAvailable &&
                        props.unitId &&
                        props.inspectionStandardType === InspectionStandardTypes.Nen2767 &&
                        props.defectId === undefined && (
                            <EditPanelRow>
                                <EditPanelCell rowCount={12}>
                                    <ValidatingFilterDropdownWithUrl
                                        name="defectId"
                                        url={`/api/surveys/${props.surveyId}/units/${props.unitId}/defects`}
                                        placeholder={translate('common.selectItem', translate('common.defect'))}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                        )}
                    <EditPanelButtons>
                        <EditPanelButton small={true} onClick={() => handleCancel()} text={translate('common.cancel')} />
                        <ValidatingEditPanelSaveButton small={true} />
                    </EditPanelButtons>
                </ValidatingForm>
            )}

            {state.selectedMaintenanceMeasure &&
                (state.formMode === FormMode.EditDefaultMaintenanceMeasurement ||
                    state.formMode === FormMode.EditObjectDefaultMaintenanceMeasurement) && (
                    <ValidatingForm
                        schema={schema}
                        onSubmitted={async () => maintenanceMeasureSaved()}
                        itemUrl={`/api/surveys/${props.surveyId}/maintenanceMeasures/${state.selectedMaintenanceMeasure.id}`}
                        itemUrlQueryParameters={{ failureModeId: props.failureModeId, defectId: props.defectId }}
                        subForm={true}
                        id={state.selectedMaintenanceMeasure.id}
                    >
                        {state.selectedMaintenanceMeasure.defaultMaintenanceMeasure.material && (
                            <EditPanelRow>
                                <EditPanelCell rowCount={2}>
                                    <strong>{translate('common.material')}:</strong>
                                </EditPanelCell>
                                <EditPanelCell rowCount={10}>
                                    {state.selectedMaintenanceMeasure.unit.quantity}
                                    <UnitOfMeasurement value={state.selectedMaintenanceMeasure.unit.quantityUnitOfMeasurement} />{' '}
                                    {state.selectedMaintenanceMeasure.defaultMaintenanceMeasure.material}
                                </EditPanelCell>
                            </EditPanelRow>
                        )}
                        <EditPanelRow>
                            <EditPanelCell rowCount={2}>
                                <ValidatingCulturedFormInputNumber name="planYear" topLabel={translate('maintenanceMeasure.year')} />
                            </EditPanelCell>
                            <EditPanelCell rowCount={2}>
                                <ValidatingCulturedFormInputNumber name="finalPlanYear" topLabel={translate('maintenanceMeasure.finalPlanYear')} />
                            </EditPanelCell>
                            <EditPanelCell rowCount={1}></EditPanelCell>
                            <EditPanelCell rowCount={2}>
                                <ValidatingCulturedFormInputNumber name="costSurcharge" topLabel={translate('maintenanceMeasure.costSurcharge')} />
                            </EditPanelCell>
                        </EditPanelRow>
                        {props.surveyIsFmecaAvailable &&
                            props.unitId &&
                            props.inspectionStandardType === InspectionStandardTypes.Fmeca &&
                            props.failureModeId === undefined && (
                                <EditPanelRow>
                                    <EditPanelCell rowCount={12}>
                                        <ValidatingFailureModeDropdown
                                            name="failureModeId"
                                            surveyId={props.surveyId}
                                            unitId={props.unitId}
                                            manifestationId={props.manifestationId}
                                            failureModeId={props.failureModeId}
                                        />
                                    </EditPanelCell>
                                </EditPanelRow>
                            )}
                        {!props.surveyIsFmecaAvailable &&
                            props.unitId &&
                            props.inspectionStandardType === InspectionStandardTypes.Nen2767 &&
                            props.defectId === undefined && (
                                <EditPanelRow>
                                    <EditPanelCell rowCount={12}>
                                        <ValidatingFilterDropdownWithUrl
                                            name="defectId"
                                            url={`/api/surveys/${props.surveyId}/units/${props.unitId}/defects`}
                                            placeholder={translate('common.selectItem', translate('common.defect'))}
                                        />
                                    </EditPanelCell>
                                </EditPanelRow>
                            )}
                        <EditPanelButtons>
                            <EditPanelButton small={true} onClick={() => handleCancel()} text={translate('common.cancel')} />
                            <ValidatingEditPanelSaveButton small={true} />
                        </EditPanelButtons>
                    </ValidatingForm>
                )}

            {relatedMaintenanceMeasure &&
                (state.formMode === FormMode.AddOverrideDefaultMaintenanceMeasurement ||
                    state.formMode === FormMode.EditOverrideDefaultMaintenanceMeasurement) && (
                    <>
                        <div className="bg-light darker p10">
                            {relatedMaintenanceMeasure.defaultMaintenanceMeasure.material && (
                                <EditPanelRow>
                                    <EditPanelCell rowCount={2}>
                                        <strong>{translate('common.material')}:</strong>
                                    </EditPanelCell>
                                    <EditPanelCell rowCount={10}>
                                        {relatedMaintenanceMeasure.unit.quantity}
                                        <UnitOfMeasurement value={relatedMaintenanceMeasure.unit.quantityUnitOfMeasurement} />{' '}
                                        {relatedMaintenanceMeasure.defaultMaintenanceMeasure.material}
                                    </EditPanelCell>
                                </EditPanelRow>
                            )}
                            <EditPanelRow>
                                <EditPanelCell rowCount={8}>
                                    <strong>{translate('survey.surveryedOn')}:</strong>&nbsp;
                                    <NullableFormattedDate value={relatedMaintenanceMeasure.survey.surveryedOn} />
                                </EditPanelCell>
                                <EditPanelCell rowCount={4}>
                                    <strong>{translate('maintenanceMeasure.costSurcharge')}:</strong>&nbsp;
                                    {relatedMaintenanceMeasure.costSurcharge && `${(relatedMaintenanceMeasure.costSurcharge * 100).toFixed(0)}%`}
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCell rowCount={4}>
                                    <strong>{translate('maintenanceMeasure.year')}:</strong>&nbsp;{relatedMaintenanceMeasure.planYear}
                                </EditPanelCell>
                                <EditPanelCell rowCount={4}>
                                    <strong>{translate('maintenanceMeasure.finalPlanYear')}:</strong>&nbsp;{relatedMaintenanceMeasure.finalPlanYear}
                                </EditPanelCell>
                                <EditPanelCell rowCount={6}>
                                    <strong>{translate('maintenanceMeasure.scheduledYear')}:</strong>&nbsp;{relatedMaintenanceMeasure.scheduledYear}
                                </EditPanelCell>
                            </EditPanelRow>
                        </div>
                        <ValidatingForm
                            schema={schema}
                            createUrl={`/api/surveys/${props.surveyId}/maintenanceMeasures`}
                            createUrlQueryParameters={{
                                unitId: props.unitId,
                                previousMaintenanceMeasureId: relatedMaintenanceMeasure.id,
                                failureModeId: props.failureModeId,
                                defectId: props.defectId,
                            }}
                            onSubmitted={async () => maintenanceMeasureSaved()}
                            subForm={true}
                            itemUrl={`/api/surveys/${props.surveyId}/maintenanceMeasures/${
                                state.selectedMaintenanceMeasure && state.selectedMaintenanceMeasure.id
                            }`}
                            itemUrlQueryParameters={{ failureModeId: props.failureModeId, defectId: props.defectId }}
                            id={state.selectedMaintenanceMeasure ? state.selectedMaintenanceMeasure.id : undefined}
                        >
                            <EditPanelRow>
                                <EditPanelCell rowCount={2}>
                                    <ValidatingCulturedFormInputNumber name="planYear" topLabel={translate('maintenanceMeasure.year')} />
                                </EditPanelCell>
                                <EditPanelCell rowCount={2}>
                                    <ValidatingCulturedFormInputNumber
                                        name="finalPlanYear"
                                        topLabel={translate('maintenanceMeasure.finalPlanYear')}
                                    />
                                </EditPanelCell>
                                <EditPanelCell rowCount={1}></EditPanelCell>
                                <EditPanelCell rowCount={2}>
                                    <ValidatingCulturedFormInputNumber
                                        name="costSurcharge"
                                        topLabel={translate('maintenanceMeasure.costSurcharge')}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCell rowCount={12}>
                                    {relatedMaintenanceMeasure && relatedMaintenanceMeasure.scheduledYear ? (
                                        <ValidatingFormTextArea
                                            name="scheduledYearRemarks"
                                            topLabel={translate('maintenanceMeasure.reasonToUpdate')}
                                        />
                                    ) : (
                                        <ValidatingFormTextArea name="planYearRemarks" topLabel={translate('maintenanceMeasure.reasonToUpdate')} />
                                    )}
                                </EditPanelCell>
                            </EditPanelRow>
                            {props.unitId && props.inspectionStandardType === InspectionStandardTypes.Fmeca && props.failureModeId === undefined && (
                                <EditPanelRow>
                                    <EditPanelCell rowCount={12}>
                                        <ValidatingFailureModeDropdown
                                            name="failureModeId"
                                            surveyId={props.surveyId}
                                            unitId={props.unitId}
                                            manifestationId={props.manifestationId}
                                            failureModeId={props.failureModeId}
                                        />
                                    </EditPanelCell>
                                </EditPanelRow>
                            )}
                            {!props.surveyIsFmecaAvailable &&
                                props.unitId &&
                                props.inspectionStandardType === InspectionStandardTypes.Nen2767 &&
                                props.defectId === undefined && (
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={12}>
                                            <ValidatingFilterDropdownWithUrl
                                                name="defectId"
                                                url={`/api/surveys/${props.surveyId}/units/${props.unitId}/defects`}
                                                placeholder={translate('common.selectItem', translate('common.defect'))}
                                            />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                )}
                            <EditPanelButtons>
                                <EditPanelButton small={true} onClick={() => handleCancel()} text={translate('common.cancel')} />
                                <ValidatingEditPanelSaveButton small={true} />
                            </EditPanelButtons>
                        </ValidatingForm>
                    </>
                )}

            {state.selectedMaintenanceMeasure && state.formMode === FormMode.EditCorrectiveMaintenanceMeasurement && (
                <ValidatingForm
                    schema={schema}
                    onSubmitted={async () => maintenanceMeasureSaved()}
                    itemUrl={`/api/surveys/${props.surveyId}/maintenanceMeasures/${state.selectedMaintenanceMeasure.id}`}
                    itemUrlQueryParameters={{ failureModeId: props.failureModeId, defectId: props.defectId }}
                    subForm={true}
                    id={state.selectedMaintenanceMeasure.id}
                >
                    <EditPanelRow>
                        <EditPanelCell rowCount={8}>
                            <ValidatingFormInputText name="description" topLabel={translate('common.description')} />
                        </EditPanelCell>
                        <EditPanelCell rowCount={4}>
                            <ValidatingFormInputText name="location" topLabel={translate('common.location')} />
                        </EditPanelCell>
                    </EditPanelRow>
                    <EditPanelRow>
                        <EditPanelCell rowCount={2}>
                            <ValidatingCulturedFormInputNumber name="planYear" topLabel={translate('maintenanceMeasure.year')} />
                        </EditPanelCell>
                        <EditPanelCell rowCount={2}>
                            <ValidatingCulturedFormInputNumber name="finalPlanYear" topLabel={translate('maintenanceMeasure.finalPlanYear')} />
                        </EditPanelCell>
                        <EditPanelCell rowCount={2}>
                            <ValidatingCulturedFormInputNumber name="cost" topLabel={translate('common.cost')} />
                        </EditPanelCell>
                        <EditPanelCell rowCount={6}>
                            <ValidatingFormInputText name="quantity" topLabel={translate('common.quantity')} />
                        </EditPanelCell>
                    </EditPanelRow>
                    {props.inspectionStandardType === InspectionStandardTypes.Fmeca && (
                        <EditPanelRow>
                            <EditPanelCell rowCount={4}>
                                <ValidatingFormInputSelect
                                    name="maintenanceType"
                                    topLabel={translate('maintenanceMeasure.maintenanceType')}
                                    placeholder={translate('common.selectItem', translate('maintenanceMeasure.maintenanceType'))}
                                    options={[
                                        {
                                            text: translate('maintenanceMeasure.correctiveMaintenance'),
                                            value: translate('maintenanceMeasure.correctiveMaintenance'),
                                        },
                                        {
                                            text: translate('maintenanceMeasure.preventiveMaintenance'),
                                            value: translate('maintenanceMeasure.preventiveMaintenance'),
                                        },
                                    ]}
                                />
                            </EditPanelCell>
                        </EditPanelRow>
                    )}
                    {props.surveyIsFmecaAvailable &&
                        props.unitId &&
                        props.inspectionStandardType === InspectionStandardTypes.Fmeca &&
                        props.failureModeId === undefined && (
                            <EditPanelRow>
                                <EditPanelCell rowCount={12}>
                                    <ValidatingFailureModeDropdown
                                        name="failureModeId"
                                        surveyId={props.surveyId}
                                        unitId={props.unitId}
                                        manifestationId={props.manifestationId}
                                        failureModeId={props.failureModeId}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                        )}
                    {!props.surveyIsFmecaAvailable &&
                        props.unitId &&
                        props.inspectionStandardType === InspectionStandardTypes.Nen2767 &&
                        props.defectId === undefined && (
                            <EditPanelRow>
                                <EditPanelCell rowCount={12}>
                                    <ValidatingFilterDropdownWithUrl
                                        name="defectId"
                                        url={`/api/surveys/${props.surveyId}/units/${props.unitId}/defects`}
                                        placeholder={translate('common.selectItem', translate('common.defect'))}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                        )}
                    <EditPanelButtons>
                        <EditPanelButton small={true} onClick={() => handleCancel()} text={translate('common.cancel')} />
                        <ValidatingEditPanelSaveButton small={true} />
                    </EditPanelButtons>
                </ValidatingForm>
            )}

            {relatedMaintenanceMeasure &&
                (state.formMode === FormMode.ShowRelatedCorrectiveMaintenanceMeasurement ||
                    state.formMode === FormMode.ShowRelatedDefaultMaintenanceMeasurement) && (
                    <>
                        <div className="bg-light darker p10">
                            {relatedMaintenanceMeasure.defaultMaintenanceMeasure.material && (
                                <EditPanelRow>
                                    <EditPanelCell rowCount={2}>
                                        <strong>{translate('common.material')}:</strong>
                                    </EditPanelCell>
                                    <EditPanelCell rowCount={10}>
                                        {relatedMaintenanceMeasure.unit.quantity}
                                        <UnitOfMeasurement value={relatedMaintenanceMeasure.unit.quantityUnitOfMeasurement} />{' '}
                                        {relatedMaintenanceMeasure.defaultMaintenanceMeasure.material}
                                    </EditPanelCell>
                                </EditPanelRow>
                            )}
                            <EditPanelRow>
                                <EditPanelCell rowCount={8}>
                                    <strong>{translate('survey.surveryedOn')}:</strong>&nbsp;
                                    <NullableFormattedDate value={relatedMaintenanceMeasure.survey.surveryedOn} />
                                </EditPanelCell>
                                <EditPanelCell rowCount={4}>
                                    <strong>{translate('maintenanceMeasure.costSurcharge')}:</strong>&nbsp;
                                    {relatedMaintenanceMeasure.costSurcharge && `${(relatedMaintenanceMeasure.costSurcharge * 100).toFixed(0)}%`}
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCell rowCount={4}>
                                    <strong>{translate('maintenanceMeasure.year')}:</strong>&nbsp;{relatedMaintenanceMeasure.planYear}
                                </EditPanelCell>
                                <EditPanelCell rowCount={4}>
                                    <strong>{translate('maintenanceMeasure.finalPlanYear')}:</strong>&nbsp;{relatedMaintenanceMeasure.finalPlanYear}
                                </EditPanelCell>
                                <EditPanelCell rowCount={4}>
                                    <strong>{translate('maintenanceMeasure.scheduledYear')}:</strong>&nbsp;{relatedMaintenanceMeasure.scheduledYear}
                                </EditPanelCell>
                            </EditPanelRow>

                            {relatedMaintenanceMeasure.planYearRemarks && (
                                <>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={4}>
                                            <strong>{translate('maintenanceMeasure.planYearRemarks')}:</strong>
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={12}>{relatedMaintenanceMeasure.planYearRemarks}</EditPanelCell>
                                    </EditPanelRow>
                                </>
                            )}

                            {relatedMaintenanceMeasure.scheduledYearRemarks && (
                                <>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={4}>
                                            <strong>{translate('maintenanceMeasure.scheduledYearRemarks')}:</strong>
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={12}>{relatedMaintenanceMeasure.scheduledYearRemarks}</EditPanelCell>
                                    </EditPanelRow>
                                </>
                            )}
                            <EditPanelButtons>
                                <EditPanelButton small={true} onClick={() => handleCancel()} text={translate('common.cancel')} />
                            </EditPanelButtons>
                        </div>
                    </>
                )}

            {currentMaintenanceMeasure && state.formMode === FormMode.ShowImplementedScheduledMaintenanceMeasurement && (
                <>
                    <div className="bg-light darker p10">
                        <EditPanelRow>
                            <EditPanelCell rowCount={4}>
                                <strong>{translate('maintenanceMeasure.implementationYear')}:</strong>&nbsp;
                                {currentMaintenanceMeasure.implementationYear}
                            </EditPanelCell>
                        </EditPanelRow>
                        <EditPanelRow>
                            <EditPanelCell rowCount={4}>
                                <strong>{translate('maintenanceMeasure.year')}:</strong>&nbsp;{currentMaintenanceMeasure.planYear}
                            </EditPanelCell>
                            <EditPanelCell rowCount={4}>
                                <strong>{translate('maintenanceMeasure.finalPlanYear')}:</strong>&nbsp;{currentMaintenanceMeasure.finalPlanYear}
                            </EditPanelCell>
                            <EditPanelCell rowCount={4}>
                                <strong>{translate('maintenanceMeasure.scheduledYear')}:</strong>&nbsp;{currentMaintenanceMeasure.scheduledYear}
                            </EditPanelCell>
                        </EditPanelRow>
                        <EditPanelButtons>
                            <EditPanelButton small={true} onClick={() => handleCancel()} text={translate('common.cancel')} />
                        </EditPanelButtons>
                    </div>
                </>
            )}
        </EditPanel>
    );
}
